import { doc, runTransaction, collection, query, where, getDocs } from 'firebase/firestore';

/** Digits in the gender suffix (e.g. M001 → 3). */
export const STUDENT_ID_SEQ_PAD = 3;

function genderToLetter(gender) {
  if (gender === 'Male') return 'M';
  if (gender === 'Female') return 'F';
  return null;
}

function padSeq(n) {
  return String(n).padStart(STUDENT_ID_SEQ_PAD, '0');
}

/**
 * Atomically allocates the next student id for a campus: PREFIX-M001, PREFIX-F002, etc.
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} campusDocId - Firestore document id of the Campus doc
 * @param {'Male'|'Female'} gender
 * @returns {Promise<string>}
 */
export async function allocateStudentId(db, campusDocId, gender) {
  const letter = genderToLetter(gender);
  if (!letter) {
    throw new Error('Gender must be Male or Female to assign a student ID.');
  }

  const campusRef = doc(db, 'Campus', campusDocId);

  const id = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(campusRef);
    if (!snap.exists()) {
      throw new Error('Campus not found.');
    }
    const data = snap.data();
    const prefix = (data.studentIdPrefix || '').trim();
    if (!prefix) {
      throw new Error('Student ID prefix is not set for this campus. Configure it in Campus Management.');
    }

    const field = letter === 'M' ? 'nextMaleSeq' : 'nextFemaleSeq';
    const current = typeof data[field] === 'number' && data[field] >= 0 ? data[field] : 0;
    const next = current + 1;
    transaction.update(campusRef, { [field]: next });
    return `${prefix}-${letter}${padSeq(next)}`;
  });

  return id;
}

export function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Max numeric suffix from student ids matching PREFIX-M### / PREFIX-F### for a campus.
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} campusCode
 * @param {string} prefix - trimmed student id prefix
 * @returns {Promise<{ nextMaleSeq: number, nextFemaleSeq: number }>}
 */
export async function computeMaxGenderSequencesFromStudents(db, campusCode, prefix) {
  const trimmed = (prefix || '').trim();
  if (!trimmed) {
    return { nextMaleSeq: 0, nextFemaleSeq: 0 };
  }

  const q = query(collection(db, 'Students'), where('campusCode', '==', campusCode));
  const snap = await getDocs(q);
  const reM = new RegExp(`^${escapeRegex(trimmed)}-M(\\d+)$`);
  const reF = new RegExp(`^${escapeRegex(trimmed)}-F(\\d+)$`);
  let maxM = 0;
  let maxF = 0;
  snap.forEach((d) => {
    const sid = d.data().id;
    if (!sid || typeof sid !== 'string') return;
    const mM = sid.match(reM);
    if (mM) maxM = Math.max(maxM, parseInt(mM[1], 10));
    const mF = sid.match(reF);
    if (mF) maxF = Math.max(maxF, parseInt(mF[1], 10));
  });
  return { nextMaleSeq: maxM, nextFemaleSeq: maxF };
}
